import { createContext, useCallback, useContext, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { Showcase } from '../../components/Showcase/Showcase';
import type { ShowcaseSpec } from '../../components/Showcase/types';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useRoleAuth } from '../../hooks/useRoleAuth';
import type { ShowcaseName } from '../../tokens/showcase';
import { useDisplaySettings } from '../DisplaySettingsProvider';

/**
 * Compares two version strings (e.g., "2.55", "2.55.1").
 * Returns: negative if a < b, positive if a > b, 0 if equal.
 */
function compareVersions(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true });
}

export interface ShowcaseContextValue {
  /** All registered showcases */
  showcases: Record<string, ShowcaseSpec>;
  /** The currently displayed showcase, if any */
  currentShowcase?: ShowcaseName;
  /** The current step index within the active showcase */
  currentStep: number;
  /** Open a showcase (will be ignored if already seen, unless force=true). Queues if another showcase is open. */
  openShowcase: (showcase: ShowcaseName, force?: boolean) => void;
  /** Close the currently displayed showcase */
  closeShowcase: () => void;
  /** Go to the next step in the current showcase */
  nextStep: () => void;
  /** Go to the previous step in the current showcase */
  prevStep: () => void;
  /** Go to a specific step */
  goToStep: (step: number) => void;
  /** Mark a showcase as seen */
  markAsSeen: (showcase: ShowcaseName) => void;
  /** Check if a showcase has been seen */
  hasBeenSeen: (showcase: ShowcaseName) => boolean;
  /** Get all showcase names that have history (been seen) */
  getShowcaseHistory: () => ShowcaseName[];
  /** Open a showcase regardless of whether it's been seen (for history viewing) */
  openShowcaseFromHistory: (showcase: ShowcaseName) => void;
  /** Get showcases the user can access (filtered by requiredFeatureFlag and requiredPermission), for picker UI */
  getAvailableShowcases: () => Array<{ name: ShowcaseName; title: string }>;
}

export const ShowcaseContext = createContext<ShowcaseContextValue | undefined>(undefined);

export function useShowcaseContext(): ShowcaseContextValue {
  const context = useContext(ShowcaseContext);
  if (context == null) {
    throw new Error('useShowcaseContext must be used within a ShowcaseProvider');
  }
  return context;
}

interface ShowcaseProviderProps {
  children: React.ReactNode;
  showcases: Record<ShowcaseName, ShowcaseSpec>;
  enable?: boolean;
}

/** Manages showcase display, state, and queuing. */
export function ShowcaseProvider({
  children,
  showcases,
  enable = import.meta.env.VITE_IS_TEST_ENVIRONMENT !== 'true',
}: PropsWithChildren<ShowcaseProviderProps>) {
  const [currentShowcase, setCurrentShowcase] = useState<ShowcaseName | undefined>();
  const [currentStep, setCurrentStep] = useState(0);
  const { firstSeenVersion, seenShowcases, setSeenShowcases } = useDisplaySettings();
  const featureFlags = useFeatureFlag();
  const { isAuthorized } = useRoleAuth();
  // Use ref for queue to avoid stale closure issues
  const queueRef = useRef<ShowcaseName[]>([]);
  const currentShowcaseRef = useRef<ShowcaseName | undefined>();

  /**
   * Sort showcases by version (older versions first, so they're shown before newer ones)
   */
  const sortByShowcaseOrder = useCallback((showcaseList: ShowcaseName[]): ShowcaseName[] => {
    return [...showcaseList].toSorted((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, []);

  /**
   * Checks if a user joined in or after the version when the showcase's feature was released.
   * If so, they shouldn't see the showcase because the feature was already available to them.
   */
  const isUserNewForShowcase = useCallback(
    (showcase: ShowcaseSpec): boolean => {
      // If firstSeenVersion is null, user existed before we started tracking - they should see all showcases
      if (firstSeenVersion == null) {
        return false;
      }

      // User is "new" for this showcase if their first version >= showcase release version
      return compareVersions(firstSeenVersion, showcase.releasedInVersion) >= 0;
    },
    [firstSeenVersion]
  );

  const hasAccessToShowcase = useCallback(
    (showcase: ShowcaseSpec): boolean => {
      // Check required feature flag
      if (showcase.requiredFeatureFlag != null) {
        const flagValue = featureFlags[showcase.requiredFeatureFlag];
        if (!flagValue) {
          return false;
        }
      }

      // Check required permission
      if (showcase.requiredPermission != null) {
        if (!isAuthorized(showcase.requiredPermission)) {
          return false;
        }
      }

      return true;
    },
    [featureFlags, isAuthorized]
  );

  /**
   * Start showing a showcase immediately
   */
  const startShowcase = useCallback((showcase: ShowcaseName) => {
    currentShowcaseRef.current = showcase;
    setCurrentShowcase(showcase);
    setCurrentStep(0);
  }, []);

  /**
   * Process the queue and start the next showcase if available
   */
  const processQueue = useCallback(() => {
    if (queueRef.current.length === 0) {
      return;
    }

    // Sort queue by showcase order and take the first
    const sortedQueue = sortByShowcaseOrder(queueRef.current);
    const nextShowcase = sortedQueue[0];

    // Remove from queue
    queueRef.current = sortedQueue.slice(1);

    // Start the showcase
    startShowcase(nextShowcase);
  }, [sortByShowcaseOrder, startShowcase]);

  const openShowcase = useCallback(
    (showcase: ShowcaseName, force = false): void => {
      if (!enable && !force) {
        return;
      }

      const showcaseSpec = showcases[showcase];
      if (showcaseSpec == null) {
        return;
      }

      // Validate user has access based on required flags and permissions
      if (!hasAccessToShowcase(showcaseSpec)) {
        return;
      }

      // Don't show to users who joined after this showcase's feature was released
      if (!force && isUserNewForShowcase(showcaseSpec)) {
        return;
      }

      // Don't show if already seen (unless forced)
      if (!force && seenShowcases[showcase]) {
        return;
      }

      // Don't add duplicates to queue
      if (queueRef.current.includes(showcase) || currentShowcaseRef.current === showcase) {
        return;
      }

      // If no showcase is currently showing, start immediately
      if (currentShowcaseRef.current == null) {
        startShowcase(showcase);
        return;
      }

      // Otherwise, add to queue
      queueRef.current = [...queueRef.current, showcase];
    },
    [enable, showcases, seenShowcases, startShowcase, hasAccessToShowcase, isUserNewForShowcase]
  );

  const openShowcaseFromHistory = useCallback(
    (showcase: ShowcaseName): void => {
      openShowcase(showcase, true);
    },
    [openShowcase]
  );

  const closeShowcase = useCallback((): void => {
    if (currentShowcaseRef.current) {
      setSeenShowcases({ ...seenShowcases, [currentShowcaseRef.current]: true });
    }
    currentShowcaseRef.current = undefined;
    setCurrentShowcase(undefined);
    setCurrentStep(0);

    // Process queue to show next showcase
    processQueue();
  }, [seenShowcases, setSeenShowcases, processQueue]);

  const nextStep = useCallback((): void => {
    if (currentShowcase == null) {
      return;
    }

    const showcase = showcases[currentShowcase];
    if (currentStep >= showcase.steps.length - 1) {
      closeShowcase();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentShowcase, currentStep, showcases, closeShowcase]);

  const prevStep = useCallback((): void => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback(
    (step: number): void => {
      if (currentShowcase == null) {
        return;
      }

      const showcase = showcases[currentShowcase];
      if (step >= 0 && step < showcase.steps.length) {
        setCurrentStep(step);
      }
    },
    [currentShowcase, showcases]
  );

  const markAsSeen = useCallback(
    (showcase: ShowcaseName): void => {
      setSeenShowcases({ ...seenShowcases, [showcase]: true });
    },
    [seenShowcases, setSeenShowcases]
  );

  const hasBeenSeen = useCallback(
    (showcase: ShowcaseName): boolean => {
      return seenShowcases[showcase] === true;
    },
    [seenShowcases]
  );

  const getShowcaseHistory = useCallback((): ShowcaseName[] => {
    return Object.keys(seenShowcases).filter(key => seenShowcases[key]) as ShowcaseName[];
  }, [seenShowcases]);

  const getAvailableShowcases = useCallback((): Array<{ name: ShowcaseName; title: string }> => {
    return (Object.keys(showcases) as ShowcaseName[])
      .filter(name => hasAccessToShowcase(showcases[name]))
      .toSorted((a, b) => compareVersions(showcases[b].releasedInVersion, showcases[a].releasedInVersion))
      .map(name => ({ name, title: showcases[name].title }));
  }, [showcases, hasAccessToShowcase]);

  const contextValue = useMemo(
    () => ({
      showcases,
      currentShowcase,
      currentStep,
      openShowcase,
      closeShowcase,
      nextStep,
      prevStep,
      goToStep,
      openShowcaseFromHistory,
      markAsSeen,
      hasBeenSeen,
      getShowcaseHistory,
      getAvailableShowcases,
    }),
    [
      showcases,
      currentShowcase,
      currentStep,
      openShowcase,
      closeShowcase,
      nextStep,
      prevStep,
      goToStep,
      openShowcaseFromHistory,
      markAsSeen,
      hasBeenSeen,
      getShowcaseHistory,
      getAvailableShowcases,
    ]
  );

  const activeShowcase = currentShowcase ? showcases[currentShowcase] : null;
  const activeStep = activeShowcase?.steps[currentStep];

  return (
    <ShowcaseContext.Provider value={contextValue}>
      {children}
      {activeShowcase && activeStep && (
        <Showcase
          isOpen={true}
          close={closeShowcase}
          steps={activeShowcase.steps}
          width={activeShowcase.width}
          height={activeShowcase.height}
          currentStep={currentStep}
          onStepChange={goToStep}
          onNext={nextStep}
          onPrev={prevStep}
          onComplete={closeShowcase}
        />
      )}
    </ShowcaseContext.Provider>
  );
}
